import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { Search, Calendar, ArrowRight } from "lucide-react";
import { getImageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"));
  const perPage = 9;
  const { settings } = useSettings();
  const pageData = settings?.news_page || {};

  const CATEGORIES = ["Semua", "Program", "Kegiatan", "Berita", "Kolaborasi", "Pengumuman"];

  useEffect(() => {
    setLoading(true);
    const params = { limit: perPage, skip: (page - 1) * perPage };
    if (q) params.q = q;
    if (category && category !== "Semua") params.category = category;
    api.get("/news", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [q, category, page]);

  useEffect(() => {
    const p = {};
    if (q) p.q = q;
    if (category && category !== "Semua") p.category = category;
    if (page > 1) p.page = page;
    setSearchParams(p, { replace: true });
  }, [q, category, page, setSearchParams]);

  const totalPages = Math.ceil(total / perPage) || 1;

  return (
    <div data-testid="page-news">
      <SeoHead title={{ id: "Berita", en: "News" }} description={{ id: pageData.description || "Berita, kabar, dan aktivitas terbaru Sauyunan Foundation di seluruh Nusantara.", en: "Latest news and activities from Sauyunan Foundation across the archipelago." }} />
      <PageHeader
        eyebrow={pageData.eyebrow || "Berita & Aktivitas"}
        title={pageData.title || "Cerita dan pembaruan dari lapangan"}
        description={pageData.description || "Ikuti kabar terbaru dari program, kegiatan, dan inisiatif Sauyunan Foundation di seluruh Nusantara."}
        image={pageData.image ? getImageUrl(pageData.image) : undefined}
      />

      <section className="section-y bg-white">
        <div className="container-sf">
          {/* Filter bar */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="news-search-input"
                type="text"
                placeholder="Cari berita..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => { setCategory(c === "Semua" ? "" : c); setPage(1); }}
                  data-testid={`news-cat-${c.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                    (category === c || (c === "Semua" && !category))
                      ? "bg-[var(--brand-blue)] text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >{c}</button>
              ))}
            </div>
          </div>

          {loading && <div className="text-center text-slate-500 py-16">Memuat...</div>}

          {!loading && items.length === 0 && (
            <div className="text-center py-24 border border-dashed border-slate-300 rounded-2xl">
              <p className="text-slate-500">Belum ada berita yang dipublikasikan.</p>
              <p className="text-xs text-slate-400 mt-2">Berita dapat ditambahkan melalui panel admin.</p>
            </div>
          )}

          {!loading && items.length > 0 && (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((n) => (
                  <Link
                    key={n.id}
                    to={`/berita/${n.slug}`}
                    className="card-sf overflow-hidden group flex flex-col"
                    data-testid={`news-item-${n.slug}`}
                  >
                    <div className="aspect-[4/2.5] overflow-hidden bg-slate-100">
                      <img
                        src={getImageUrl(n.cover_image || "https://images.unsplash.com/photo-1747778148316-4a8eeb132d48?w=900&q=80")}
                        alt={n.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                        <span className="inline-flex items-center rounded-full bg-[var(--brand-soft-blue)] text-[var(--brand-blue)] px-3 py-1 font-semibold uppercase tracking-wider text-[10px]">
                          {n.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />{" "}
                          {new Date(n.published_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-[var(--brand-blue)] transition line-clamp-2">
                        {n.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">{n.excerpt}</p>
                      <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-[var(--brand-orange)]">
                        Baca <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-2">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`h-10 w-10 rounded-full text-sm font-semibold transition ${
                        page === i + 1 ? "bg-[var(--brand-blue)] text-white" : "bg-white border border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`news-page-${i + 1}`}
                    >{i + 1}</button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
